// Shared quick-action cluster for the floating toolbars (Canva/Figma-style):
// Duplicate + Delete buttons plus a More menu (lock, layer order, and a
// duplicate/delete fallback). Used by both ImageFloatingToolbar and
// TextFloatingToolbar so every selectable element exposes the same actions.
//
// All callbacks are provided by the caller (EditorPage), which owns history
// snapshots — Duplicate and Delete each create one undo entry there.

import React from 'react';
import {
  Copy, Trash2, MoreHorizontal, Lock, Unlock,
  ArrowUp, ArrowDown, ChevronsUp, ChevronsDown,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';

const IconBtn = ({ title, onClick, danger, children }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    onClick={onClick}
    className={cn(
      'h-7 w-7 flex items-center justify-center rounded-md transition-colors',
      danger
        ? 'text-rose-600 hover:text-rose-700 hover:bg-rose-50'
        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
    )}
  >
    {children}
  </button>
);

const MenuItem = ({ icon: Icon, label, onClick, danger }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs text-left transition-colors',
      danger ? 'text-rose-600 hover:bg-rose-50' : 'text-gray-700 hover:bg-gray-100'
    )}
  >
    <Icon className="w-3.5 h-3.5 flex-shrink-0" />
    {label}
  </button>
);

const ElementQuickActions = ({
  element,
  onDuplicate,
  onDelete,
  onToggleLock,
  onLayerOrder, // (direction: 'up' | 'down' | 'top' | 'bottom')
}) => {
  if (!element) return null;
  const locked = !!element.locked;

  return (
    <div className="flex items-center gap-0.5" data-testid="element-quick-actions">
      <IconBtn title="Duplicate" onClick={onDuplicate}>
        <Copy className="w-4 h-4" />
      </IconBtn>
      <IconBtn title="Delete" onClick={onDelete} danger>
        <Trash2 className="w-4 h-4" />
      </IconBtn>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            title="More"
            aria-label="More actions"
            data-testid="quick-actions-more"
            className="h-7 w-7 flex items-center justify-center rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" side="bottom" sideOffset={6} className="w-48 p-1">
          <MenuItem icon={Copy} label="Duplicate" onClick={onDuplicate} />
          <MenuItem icon={locked ? Unlock : Lock} label={locked ? 'Unlock' : 'Lock'} onClick={onToggleLock} />
          <div className="my-1 h-px bg-gray-100" />
          <MenuItem icon={ChevronsUp} label="Bring to front" onClick={() => onLayerOrder('top')} />
          <MenuItem icon={ArrowUp} label="Bring forward" onClick={() => onLayerOrder('up')} />
          <MenuItem icon={ArrowDown} label="Send backward" onClick={() => onLayerOrder('down')} />
          <MenuItem icon={ChevronsDown} label="Send to back" onClick={() => onLayerOrder('bottom')} />
          <div className="my-1 h-px bg-gray-100" />
          <MenuItem icon={Trash2} label="Delete" onClick={onDelete} danger />
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default ElementQuickActions;
